import { imgSrcSet } from "../lib/media";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Wallet, AlertTriangle, Save, TreePine, ChevronDown, Search, Sparkles, Clock, Trash2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useConfig, symbolForCurrency, nf, formatMoney } from "../lib/format";
import { PageHeader, Loader } from "../components/shared";
import { Modal } from "../components/Modal";
import ProductResults from "../components/ProductResults";
import { ProductSearchBar } from "../components/ProductSearchBar";
import { useBudgetAllocate } from "../components/BudgetAllocate";
import { SaveName } from "../components/SaveName";
import { SaveChangesBar } from "../components/SaveChangesBar";
import { PageTutorial } from "../components/PageTutorial";
import { DECORATION_TUTORIAL } from "../tutorials/decoration";

export default function Decoration() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const config = useConfig();
  const { allocate, node: allocNode } = useBudgetAllocate();
  const [data, setData] = useState(null);
  const [total, setTotal] = useState("");
  const [authorized, setAuthorized] = useState(0);
  const [style, setStyle] = useState("elegant");
  const [detailStyle, setDetailStyle] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [slotModal, setSlotModal] = useState(null); // {label, result, loading}
  const [saves, setSaves] = useState([]);
  const [saveView, setSaveView] = useState(null); // a saved snapshot being viewed
  const [savedDirty, setSavedDirty] = useState(false); // unsaved edits inside saveView
  const [params] = useSearchParams();
  const wantedSave = params.get("save");
  useEffect(() => {
    if (!wantedSave) return;
    const sv = saves.find((x) => x.save_id === wantedSave);
    if (sv) setSaveView(sv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedSave, saves.length]);

  const country = user?.country || "BR";
  const draftKey = `deco_plan_draft_${user?.user_id || "anon"}`;

  // Keep the built plan across navigation/reload (it used to vanish when leaving the page).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) { const d = JSON.parse(raw); if (d?.plan?.slots) { setPlan(d.plan); if (d.style) setStyle(d.style); } }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);
  useEffect(() => {
    try { if (plan?.slots) localStorage.setItem(draftKey, JSON.stringify({ plan, style })); } catch { /* ignore */ }
  }, [plan, style, draftKey]);

  useEffect(() => {
    api.get(`/content/decoration?lang=${lang}`).then(({ data }) => setData(data)).catch(() => {});
    // Single source of truth: prefill with the allocated Decoration budget from /budget.
    api.get("/budget").then(({ data }) => {
      const lim = data.budget?.categories?.decoration?.limit;
      if (lim != null) { setTotal(lim); setAuthorized(lim); }
    }).catch(() => {});
    api.get("/decoration/saves").then(({ data }) => setSaves(data.saves || [])).catch(() => {});
  }, [lang]);

  if (!data) return <Loader />;
  const symbol = symbolForCurrency(config, user?.currency || "BRL");
  const tiers = config?.budget_tiers?.decoration?.[user?.currency || "BRL"] || [];
  const totalNum = Number(total || 0);
  const fmt = (v) => formatMoney(symbol, v || 0);

  // Central validation funnel: any requested budget above the AUTHORIZED (persisted)
  // amount is blocked and routed through the "Rever Meu Orçamento" flow. It never
  // silently changes the authorized budget.
  const requestBudget = (value) => {
    const req = Number(value) || 0;
    setTotal(req);
    if (req <= authorized) return;
    allocate("decoration", req, (b, cancelled) => {
      if (cancelled || !b) { setTotal(authorized); return; }
      const lim = b?.categories?.decoration?.limit;
      const na = lim != null ? lim : authorized;
      setAuthorized(na); setTotal(na);
    });
  };

  const buildPlan = async (styleId) => {
    if (!totalNum) { toast.error(t("deco.set_total")); return; }
    // Second defensive layer: do not build with an unauthorized budget.
    if (totalNum > authorized) { requestBudget(totalNum); return; }
    setPlanLoading(true); setPlan(null);
    try {
      const { data: p } = await api.get(`/products/decor-plan?budget=${totalNum}&style=${styleId}&country=${country}&lang=${lang}`);
      setPlan(p);
    } catch { toast.error(t("prod.error")); }
    setPlanLoading(false);
  };
  const chooseStyle = (s) => { setStyle(s.id); setDetailStyle(null); buildPlan(s.id); };
  const persistSave = async () => {
    if (!plan || !plan.slots) return;
    try {
      const { data } = await api.post("/decoration/saves", {
        style, total: plan.budget ?? totalNum,
        spent_estimate: plan.spent_estimate, remaining_estimate: plan.remaining_estimate,
        slots: plan.slots.filter((s) => s.product).map((s) => ({
          id: s.id, label: s.label, allocated: s.allocated, quantity: s.quantity, line_total: s.line_total, product: s.product,
        })),
      });
      setSaves(data.saves || []);
    } catch { /* non-blocking */ }
  };
  const saveBudget = () => {
    if (plan && saves.length >= 3) { toast.error(t("deco.saves_full")); return; }
    allocate("decoration", totalNum, async (b, cancelled) => {
      if (cancelled || !b) { setTotal(authorized); return; }
      const lim = b?.categories?.decoration?.limit;
      const na = lim != null ? lim : authorized;
      setAuthorized(na); setTotal(na);
      await persistSave();
      setPlan(null);
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      toast.success(t("deco.saved"));
      setTimeout(() => document.querySelector('[data-testid="decoration-budget"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    });
  };
  const deleteSave = async (id) => {
    try { const { data } = await api.delete(`/decoration/saves/${id}`); setSaves(data.saves || []); }
    catch { /* ignore */ }
  };
  const patchSave = async (id, body) => {
    try {
      const { data } = await api.patch(`/decoration/saves/${id}`, body);
      const list = data.saves || [];
      setSaves(list);
      if (saveView?.save_id === id) setSaveView(list.find((x) => x.save_id === id) || null);
      toast.success(t("saves.updated"));
    } catch { toast.error(t("prod.error")); }
  };
  const unitPrice = (sl) => Number(sl.product?.extracted_price ?? (sl.quantity ? Number(sl.line_total || 0) / sl.quantity : 0)) || 0;
  // Edits inside a saved snapshot stay local until "Save changes".
  const applyDraft = (sv, slots) => {
    const spent = Math.round(slots.reduce((a, sl) => a + Number(sl.line_total || 0), 0) * 100) / 100;
    setSaveView({ ...sv, slots, spent_estimate: spent, remaining_estimate: Math.round(Number(sv.total || 0) - spent) });
    setSavedDirty(true);
  };
  const setSlotQty = (sv, idx, qty) => applyDraft(sv, (sv.slots || []).map((sl, i) => i === idx ? { ...sl, quantity: qty, line_total: Math.round(unitPrice(sl) * qty * 100) / 100 } : sl));
  const removeSlot = (sv, idx) => applyDraft(sv, (sv.slots || []).filter((_, i) => i !== idx));
  const saveDraft = async () => { await patchSave(saveView.save_id, { slots: saveView.slots || [] }); setSavedDirty(false); };
  const discardDraft = () => { setSaveView(saves.find((x) => x.save_id === saveView?.save_id) || null); setSavedDirty(false); };
  const closeSaveView = () => { setSaveView(null); setSavedDirty(false); };
  const overOf = (sv) => Math.round((Number(sv?.spent_estimate || 0) - Number(authorized || 0)) * 100) / 100;
  const styleName = (id) => data.styles.find((x) => x.id === id)?.name || id;
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(lang === "pt-PT" ? "pt-PT" : lang === "en" ? "en-US" : lang === "es" ? "es-ES" : lang === "fr" ? "fr-FR" : lang === "it" ? "it-IT" : "pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };
  const openSlotOptions = async (slot) => {
    setSlotModal({ slotId: slot.id, label: slot.label, allocated: slot.allocated, loading: true, result: null });
    try {
      const { data: res } = await api.get(`/products/search?q=${encodeURIComponent(slot.query)}&country=${country}&max_price=${Math.round(slot.allocated * 1.3)}&limit=12&min_results=10`);
      setSlotModal((m) => ({ ...m, loading: false, result: res }));
    } catch { setSlotModal((m) => ({ ...(m || {}), loading: false, result: { state: "error" } })); }
  };
  const openCustomSearch = async (q) => {
    const room = Math.max(0, Number(plan?.remaining_estimate ?? totalNum) || 0);
    setSlotModal({ custom: { id: `custom_${Date.now()}`, query: q }, label: q, allocated: room, loading: true, result: null });
    try {
      const maxP = room >= 1 ? `&max_price=${Math.round(room)}` : "";
      const { data: res } = await api.get(`/products/search?q=${encodeURIComponent(q)}&country=${country}${maxP}&limit=12&min_results=10&diverse=1`);
      setSlotModal((m) => ({ ...m, loading: false, result: res }));
    } catch { setSlotModal((m) => ({ ...(m || {}), loading: false, result: { state: "error" } })); }
  };
  // Swap a product inside a saved snapshot: search must fit the room left in the CURRENT budget.
  const savedSlotRoom = (sv, idx) => {
    const sl = (sv.slots || [])[idx];
    const others = Number(sv.spent_estimate || 0) - Number(sl?.line_total || 0);
    const room = Number(authorized || 0) - others;
    return Math.max(0, Math.round(Math.min(sl?.allocated || Infinity, room) * 100) / 100);
  };
  const openSavedSlotOptions = async (sv, idx) => {
    const sl = (sv.slots || [])[idx];
    const room = savedSlotRoom(sv, idx);
    setSlotModal({ saved: { saveId: sv.save_id, idx }, label: sl.label, allocated: room, loading: true, result: null });
    try {
      const maxP = room >= 1 ? `&max_price=${Math.round(room)}` : "";
      const url = sl.id
        ? `/products/slot-options?slot_id=${encodeURIComponent(sl.id)}&style=${encodeURIComponent(sv.style || style)}&country=${country}${maxP}`
        : `/products/search?q=${encodeURIComponent(sl.query || sl.label || sl.product?.title || "")}&country=${country}${maxP}&limit=12&min_results=10`;
      const { data: res } = await api.get(url);
      setSlotModal((m) => ({ ...m, loading: false, result: res }));
    } catch { setSlotModal((m) => ({ ...(m || {}), loading: false, result: { state: "error" } })); }
  };
  const chooseProduct = (product) => {
    if (!slotModal) return;
    const allocated = slotModal.allocated || 0;
    const price = product.extracted_price;
    let quantity = 1, line_total = 0;
    if (price && price > 0) {
      const maxUnits = Math.min(4, Math.floor(allocated / price));
      quantity = maxUnits >= 1 ? maxUnits : 1;
      line_total = Math.round(quantity * price * 100) / 100;
    }
    if (slotModal.saved) {
      if (saveView?.save_id === slotModal.saved.saveId) {
        applyDraft(saveView, (saveView.slots || []).map((sl, i) => i === slotModal.saved.idx ? { ...sl, product, quantity, line_total } : sl));
      }
      setSlotModal(null);
      return;
    }
    if (!plan) return;
    const newSlots = slotModal.custom
      ? [...plan.slots, { id: slotModal.custom.id, label: slotModal.label, query: slotModal.custom.query, allocated, product, quantity: 1, line_total: Math.round((price || 0) * 100) / 100 }]
      : plan.slots.map((s) => s.id === slotModal.slotId ? { ...s, product, quantity, line_total } : s);
    const spent = newSlots.reduce((acc, s) => acc + (s.product?.extracted_price ? (s.line_total || 0) : 0), 0);
    setPlan({ ...plan, slots: newSlots, spent_estimate: Math.round(spent * 100) / 100, remaining_estimate: Math.round(((plan.budget || totalNum) - spent) * 100) / 100 });
    setSlotModal(null);
  };
  const setSlotQuantity = (slotId, qty) => {
    if (!plan) return;
    const newSlots = plan.slots.map((s) => {
      if (s.id !== slotId) return s;
      const price = s.product?.extracted_price || 0;
      const q = Math.max(1, qty);
      return { ...s, quantity: q, line_total: Math.round(q * price * 100) / 100 };
    });
    const spent = newSlots.reduce((acc, s) => acc + (s.product?.extracted_price ? (s.line_total || 0) : 0), 0);
    setPlan({ ...plan, slots: newSlots, spent_estimate: Math.round(spent * 100) / 100, remaining_estimate: Math.round(((plan.budget || totalNum) - spent) * 100) / 100 });
  };

  const over = plan && plan.remaining_estimate < 0;

  return (
    <div>
      <PageHeader title={t("deco.title")} testid="decoration-header" />
      <PageTutorial id="decoration" content={DECORATION_TUTORIAL} />

      {/* Style cards */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
        {data.styles.map((s) => (
          <button key={s.id} onClick={() => setDetailStyle(s)} data-testid={`deco-style-${s.id}`}
            className="card-lift group relative overflow-hidden rounded-2xl border border-xmas-gold/50 text-left shadow-[0_0_0_1px_rgba(212,175,55,0.25),0_0_18px_rgba(212,175,55,0.25)] transition hover:border-xmas-gold/80 hover:shadow-[0_0_22px_rgba(212,175,55,0.35)]">
            <div className="relative h-56">
              <img src={s.img} srcSet={imgSrcSet(s.img)} sizes="(max-width: 640px) 100vw, 400px" alt={s.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-xmas-navy via-xmas-navy/30 to-transparent" />
            </div>
            <div className="absolute inset-x-0 bottom-0 p-5">
              <div className="mb-2 flex gap-1.5">
                {s.palette.map((c, i) => <span key={i} className="h-4 w-4 rounded-full border border-white/30" style={{ background: c }} />)}
              </div>
              <h3 className="font-display text-xl font-semibold text-xmas-snow">{s.name}</h3>
              <p className="mt-1 line-clamp-2 text-sm text-xmas-snow/75">{s.desc}</p>
              <span className="btn-gold mt-4 inline-block rounded-full px-5 py-2 text-xs font-bold uppercase tracking-wider">{t("deco.view_style")}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Budget tool */}
      <div className="glass mb-8 rounded-2xl p-6" data-testid="decoration-budget">
        <div className="mb-1 flex items-center gap-2">
          <Wallet className="h-5 w-5 text-xmas-gold" />
          <h2 className="font-display text-lg text-xmas-snow">{t("deco.budget_tool")}</h2>
        </div>
        <p className="mb-4 text-sm text-xmas-slate">{t("deco.budget_tool_sub")}</p>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-5 py-3 font-mono-num text-2xl font-bold text-xmas-gold" data-testid="deco-total-display">{fmt(totalNum)}</span>
              {saves.length > 0 && (() => {
                const savedSum = Math.round(saves.reduce((a, s) => a + Number(s.spent_estimate || 0), 0) * 100) / 100;
                const avail = Math.round((Number(authorized || 0) - savedSum) * 100) / 100;
                return (
                  <div className="flex flex-col text-xs" data-testid="deco-saves-available">
                    <span className="text-xmas-slate">{t("deco.saves_title")}: <b className="font-mono-num text-xmas-snow" data-testid="deco-saves-sum">{fmt(savedSum)}</b></span>
                    <span className={avail < 0 ? "text-xmas-crimson" : "text-xmas-champagne"}>{t("deco.remaining")}: <b className="font-mono-num" data-testid="deco-saves-avail">{fmt(avail)}</b>{avail < 0 && <AlertTriangle className="ml-1 inline h-3 w-3" />}</span>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Saved snapshots history (max 3) — beside the budget */}
          <div className="border-t border-xmas-gold/15 pt-4 md:border-l md:border-t-0 md:pl-6 md:pt-0" data-testid="deco-saves">
            <div className="mb-1 flex items-center gap-2">
              <Clock className="h-4 w-4 text-xmas-gold" />
              <h3 className="font-display text-sm text-xmas-snow">{t("deco.saves_title")}</h3>
              <span className="text-[11px] text-xmas-slate">({saves.length}/3)</span>
            </div>
            <p className="mb-3 text-xs text-xmas-slate">{t("deco.saves_hint")}</p>
            {saves.length === 0 ? (
              <p className="text-xs text-xmas-slate">{t("deco.saves_empty")}</p>
            ) : (
              <div className="space-y-2">
                {saves.map((s) => (
                  <div key={s.save_id} data-testid={`deco-save-${s.save_id}`} className="flex items-center justify-between gap-2 rounded-xl bg-xmas-navy/40 px-3 py-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <button onClick={() => setSaveView(s)} data-testid={`deco-save-view-${s.save_id}`} aria-label="view" className="shrink-0"><Clock className="h-3.5 w-3.5 text-xmas-gold" /></button>
                      <SaveName name={s.name} fallback={fmtDate(s.saved_at)} placeholder={t("saves.name_ph")} testid={`deco-save-name-${s.save_id}`} onClick={() => setSaveView(s)} onRename={(v) => patchSave(s.save_id, { name: v })} />
                      {s.name && <span className="shrink-0 text-[11px] text-xmas-slate">{fmtDate(s.saved_at)}</span>}
                      <span className="hidden shrink-0 rounded-full bg-xmas-gold/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-xmas-champagne sm:inline" data-testid={`deco-save-style-${s.save_id}`}>{styleName(s.style)}</span>
                      <button onClick={() => setSaveView(s)} className={`inline-flex shrink-0 items-center gap-1 font-mono-num text-xs ${overOf(s) > 0 ? "text-xmas-crimson" : "text-xmas-champagne"}`} data-testid={`deco-save-total-${s.save_id}`}>
                        {overOf(s) > 0 && <AlertTriangle className="h-3 w-3" />}{fmt(s.spent_estimate)}
                      </button>
                    </div>
                    <button onClick={() => deleteSave(s.save_id)} data-testid={`deco-save-delete-${s.save_id}`} aria-label="delete" className="shrink-0 rounded-full p-1.5 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Plan result */}
      {(planLoading || plan) && (
        <div className="glass mb-8 rounded-2xl p-6" data-testid="deco-plan">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="font-display text-xl text-xmas-gold">{t("deco.plan_title")}</h2>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-xmas-gold/15 px-3 py-1 text-xs font-semibold text-xmas-gold" data-testid="deco-plan-style">
                <TreePine className="h-3.5 w-3.5" /> {styleName(plan?.style || style)}
              </span>
            </div>
            {plan && (
              <button type="button" onClick={() => { setPlan(null); try { localStorage.removeItem(draftKey); } catch { /* ignore */ } }} data-testid="deco-plan-discard"
                className="inline-flex items-center gap-1.5 rounded-full border border-xmas-crimson/40 px-3 py-1.5 text-xs text-xmas-snow transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson">
                <Trash2 className="h-3.5 w-3.5" /> {t("deco.discard_plan")}
              </button>
            )}
          </div>
          {planLoading ? (
            <ProductResults loading result={null} />
          ) : (
            <>
              {plan.state === "not_configured" && (
                <div className="mb-4 rounded-xl border border-xmas-gold/30 bg-xmas-gold/10 p-4 text-sm text-xmas-champagne" data-testid="deco-plan-not-configured">
                  {t("prod.not_configured")}
                </div>
              )}
              <div className="mb-4">
                <ProductSearchBar historyKey="decoration" testid="deco-search" onSearch={openCustomSearch} maxLabel={plan.remaining_estimate > 0 ? fmt(plan.remaining_estimate) : null} />
              </div>
              <div className="space-y-3">
                {plan.slots.map((slot) => (
                  <div key={slot.id} className="rounded-xl bg-xmas-navy/40 p-4" data-testid={`deco-slot-${slot.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium text-xmas-snow">{slot.label}</span>
                      <span className="font-mono-num text-sm text-xmas-champagne">{t("deco.allocated")}: {fmt(slot.allocated)} <span className="text-[10px] text-xmas-slate">({t("estimate")})</span></span>
                    </div>
                    {slot.product && (
                      <div className="mt-3 flex gap-3">
                        {slot.product.thumbnail && <img src={slot.product.thumbnail} alt="" className="h-16 w-16 rounded-lg bg-white object-contain" />}
                        <div className="min-w-0 flex-1">
                          <p className="line-clamp-2 text-sm text-xmas-snow">{slot.product.title}</p>
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            {slot.product.price && <span className="font-mono-num text-xmas-gold">{slot.product.price}</span>}
                            {slot.product.source && <span className="text-xmas-slate">{slot.product.source}</span>}
                          </div>
                          {slot.product.extracted_price > 0 && (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <div className="inline-flex items-center gap-1 rounded-full border border-xmas-gold/25 bg-xmas-navy/60 px-1">
                                <button onClick={() => setSlotQuantity(slot.id, slot.quantity - 1)} disabled={slot.quantity <= 1} data-testid={`deco-qty-minus-${slot.id}`}
                                  className="flex h-6 w-6 items-center justify-center rounded-full text-xmas-gold transition hover:bg-xmas-gold/15 disabled:opacity-30" aria-label="minus"><Minus className="h-3.5 w-3.5" /></button>
                                <span data-testid={`deco-qty-${slot.id}`} className="w-6 text-center font-mono-num text-sm text-xmas-snow">{slot.quantity}</span>
                                <button onClick={() => setSlotQuantity(slot.id, slot.quantity + 1)} disabled={(slot.quantity + 1) * slot.product.extracted_price > totalNum} data-testid={`deco-qty-plus-${slot.id}`}
                                  className="flex h-6 w-6 items-center justify-center rounded-full text-xmas-gold transition hover:bg-xmas-gold/15 disabled:opacity-30" aria-label="plus"><Plus className="h-3.5 w-3.5" /></button>
                              </div>
                              <span className="font-mono-num text-xs text-xmas-champagne">= {fmt(slot.line_total)}</span>
                            </div>
                          )}
                          {slot.product.product_link && <a href={slot.product.product_link} target="_blank" rel="noreferrer" className="mt-1 block text-xs text-xmas-gold hover:underline">{t("prod.view")}</a>}
                        </div>
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button onClick={() => openSlotOptions(slot)} data-testid={`deco-slot-others-${slot.id}`} className="inline-flex items-center gap-1 rounded-full border border-xmas-gold/30 px-3 py-1.5 text-xs text-xmas-snow hover:border-xmas-gold">
                        <Search className="h-3 w-3" /> {t("deco.see_others")}
                      </button>
                      {String(slot.id).startsWith("custom_") && (
                        <button onClick={() => { const ns = plan.slots.filter((s) => s.id !== slot.id); const sp = ns.reduce((a, s) => a + (s.product?.extracted_price ? (s.line_total || 0) : 0), 0); setPlan({ ...plan, slots: ns, spent_estimate: Math.round(sp * 100) / 100, remaining_estimate: Math.round(((plan.budget || totalNum) - sp) * 100) / 100 }); }}
                          data-testid={`deco-slot-remove-${slot.id}`} title={t("saves.remove_item")} className="inline-flex items-center gap-1 rounded-full border border-xmas-crimson/40 px-3 py-1.5 text-xs text-xmas-snow hover:bg-xmas-crimson/20 hover:text-xmas-crimson">
                          <Trash2 className="h-3 w-3" /> {t("saves.remove_item")}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {plan.configured && (
                <div className="mt-4 flex flex-wrap gap-4">
                  <span className="text-sm text-xmas-slate">{t("deco.distributed")}: <b className="font-mono-num text-xmas-snow">{fmt(plan.spent_estimate)}</b></span>
                  <span className={`text-sm ${over ? "text-xmas-crimson" : "text-xmas-slate"}`}>{t("deco.remaining")}: <b className="font-mono-num">{fmt(plan.remaining_estimate)}</b></span>
                </div>
              )}
              {over && (
                <div className="mt-3 flex items-center gap-2 rounded-xl border border-xmas-crimson/40 bg-xmas-crimson/10 px-4 py-2.5 text-sm text-xmas-crimson" data-testid="deco-over-alert">
                  <AlertTriangle className="h-4 w-4 shrink-0" /> {t("deco.over", { v: fmt(Math.abs(plan.remaining_estimate)) })}
                </div>
              )}
              <button onClick={saveBudget} data-testid="deco-save-budget" className="btn-gold mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider">
                <Save className="h-4 w-4" /> {t("deco.save_budget")}
              </button>
            </>
          )}
        </div>
      )}

      {/* Tree assembly guide */}
      {data.tree_guide?.length > 0 && (
        <div className="glass mb-4 overflow-hidden rounded-2xl" data-testid="tree-guide">
          <button onClick={() => setGuideOpen((o) => !o)} className="flex w-full items-center justify-between px-6 py-4 text-left">
            <span className="flex items-center gap-2 font-display text-lg text-xmas-snow">
              <TreePine className="h-5 w-5 text-xmas-pine" /> {t("deco.tree_guide_title")}
            </span>
            <ChevronDown className={`h-5 w-5 text-xmas-gold transition ${guideOpen ? "rotate-180" : ""}`} />
          </button>
          {guideOpen && (
            <ol className="space-y-3 px-6 pb-5">
              {data.tree_guide.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm text-xmas-snow">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-xmas-gold/20 font-mono-num text-xs text-xmas-gold">{i + 1}</span>{step}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {/* Style detail modal */}
      <Modal open={!!detailStyle} onClose={() => setDetailStyle(null)} title={detailStyle?.name} testid="deco-style-modal">
        {detailStyle && (
          <div>
            <img src={detailStyle.img} srcSet={imgSrcSet(detailStyle.img)} sizes="(max-width: 640px) 100vw, 640px" alt={detailStyle.name} decoding="async" className="h-48 w-full rounded-xl object-cover" />
            <div className="mt-3 flex gap-1.5">
              {detailStyle.palette.map((c, i) => <span key={i} className="h-5 w-5 rounded-full border border-white/30" style={{ background: c }} />)}
            </div>
            <p className="mt-4 text-sm text-xmas-slate">{detailStyle.long_desc || detailStyle.desc}</p>
            <p className="mt-4 text-xs uppercase tracking-widest text-xmas-champagne">{t("deco.ideal_for")}</p>
            <p className="text-sm italic text-xmas-snow">"{detailStyle.ideal_for}"</p>
            {detailStyle.elements?.length > 0 && (
              <>
                <p className="mt-4 text-xs uppercase tracking-widest text-xmas-champagne">{t("deco.elements")}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {detailStyle.elements.map((e, i) => <span key={i} className="rounded-full bg-xmas-navy/60 px-3 py-1 text-xs text-xmas-snow">{e}</span>)}
                </div>
              </>
            )}
            <button onClick={() => chooseStyle(detailStyle)} data-testid="deco-build-style" className="btn-gold mt-6 w-full rounded-full py-3 text-sm font-bold uppercase tracking-wider">
              {t("deco.build")} {detailStyle.name}
            </button>
          </div>
        )}
      </Modal>

      {/* Saved snapshot items modal */}
      <Modal open={!!saveView} onClose={() => { setSaveView(null); setSavedDirty(false); }} title={saveView ? `${saveView.name || t("deco.saved_items")} · ${fmtDate(saveView.saved_at)}` : ""} testid="deco-save-view-modal">
        {saveView && (
          <div>
            <div className="mb-3 flex flex-wrap items-center gap-4 text-sm">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-xmas-gold/15 px-3 py-1 text-xs font-semibold text-xmas-gold" data-testid="deco-saved-style"><TreePine className="h-3.5 w-3.5" /> {styleName(saveView.style)}</span>
              <span className="text-xmas-slate">{t("deco.distributed")}: <b className={`font-mono-num ${overOf(saveView) > 0 ? "text-xmas-crimson" : "text-xmas-snow"}`} data-testid="deco-saved-spent">{fmt(saveView.spent_estimate)}</b></span>
              <span className="text-xmas-slate">{t("deco.remaining")}: <b className={`font-mono-num ${overOf(saveView) > 0 ? "text-xmas-crimson" : "text-xmas-snow"}`}>{fmt(Number(authorized || 0) - Number(saveView.spent_estimate || 0))}</b></span>
            </div>
            {overOf(saveView) > 0 && (
              <div className="mb-4 flex items-start gap-2 rounded-xl border border-xmas-crimson/40 bg-xmas-crimson/15 px-4 py-2.5 text-sm text-xmas-snow" data-testid="deco-saved-over-budget">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-xmas-crimson" />
                <span>{t("saves.over_budget").replace("{s}", fmt(saveView.spent_estimate)).replace("{v}", fmt(overOf(saveView))).replace("{b}", fmt(authorized))}</span>
              </div>
            )}
            <div className="space-y-3">
              {(saveView.slots || []).map((s, i) => (
                <div key={i} className="flex gap-3 rounded-xl bg-xmas-navy/40 p-3" data-testid="deco-saved-item">
                  {s.product?.thumbnail && <img src={s.product.thumbnail} alt="" className="h-16 w-16 rounded-lg bg-white object-contain" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs uppercase tracking-wide text-xmas-champagne">{s.label}</p>
                    <p className="line-clamp-2 text-sm text-xmas-snow">{s.product?.title}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                      {s.product?.price && <span className="font-mono-num text-xmas-gold">{s.product.price}</span>}
                      <span className="inline-flex items-center gap-1 rounded-full border border-xmas-gold/25 px-1.5 py-0.5">
                        <button type="button" onClick={() => setSlotQty(saveView, i, Math.max(1, (s.quantity || 1) - 1))} disabled={(s.quantity || 1) <= 1} data-testid={`deco-saved-qty-minus-${i}`} className="rounded-full p-0.5 text-xmas-snow hover:bg-xmas-gold/20 disabled:opacity-30"><Minus className="h-3 w-3" /></button>
                        <span className="font-mono-num min-w-[1.25rem] text-center text-xmas-snow" data-testid={`deco-saved-qty-${i}`}>{s.quantity || 1}</span>
                        <button type="button" onClick={() => setSlotQty(saveView, i, (s.quantity || 1) + 1)} data-testid={`deco-saved-qty-plus-${i}`} className="rounded-full p-0.5 text-xmas-snow hover:bg-xmas-gold/20"><Plus className="h-3 w-3" /></button>
                      </span>
                      <span className="font-mono-num text-xmas-champagne">= {fmt(s.line_total)}</span>
                      {s.product?.source && <span className="text-xmas-slate">{s.product.source}</span>}
                    </div>
                    {s.product?.product_link && <a href={s.product.product_link} target="_blank" rel="noreferrer" className="text-xs text-xmas-gold hover:underline">{t("prod.view")}</a>}
                  </div>
                  <div className="flex shrink-0 flex-col items-center gap-1">
                    <button type="button" onClick={() => openSavedSlotOptions(saveView, i)} data-testid={`deco-saved-swap-${i}`} aria-label={t("deco.see_others")} title={t("deco.see_others")}
                      className="rounded-full p-1.5 text-xmas-slate transition hover:bg-xmas-gold/20 hover:text-xmas-gold">
                      <Search className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => removeSlot(saveView, i)} data-testid={`deco-saved-remove-${i}`} aria-label={t("saves.remove_item")} title={t("saves.remove_item")}
                      className="rounded-full p-1.5 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {savedDirty && <SaveChangesBar t={t} onSave={saveDraft} onDiscard={discardDraft} testid="deco-saved-changes" />}
          </div>
        )}
      </Modal>
      {/* Slot options modal */}
      <Modal open={!!slotModal} onClose={() => setSlotModal(null)} title={slotModal?.label} testid="deco-slot-modal">
        <ProductResults loading={slotModal?.loading} result={slotModal?.result} onChoose={chooseProduct} />
      </Modal>
      {allocNode}
    </div>
  );
}
